import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Checkbox } from '@gpstrackit/gtc-react-controls';
import { Close as CloseIcon } from '@mui/icons-material';
import { fetchMechanics } from '../redux/slice';

export const REPAIR_MODE = {
  REQUIRED: 'required',
  UNNECESSARY: 'unnecessary',
  PENDING: 'pending',
};

const MAX_COMMENT_LENGTH = 500;

const RepairModal = ({ open, onClose, onSubmit, defect, defects: defectsProp, userInfo, initialMode }) => {
  // Support both single defect (backward compat) and multiple defects
  const defects = defectsProp || (defect ? [defect] : []);
  const dispatch = useDispatch();
  const { messages } = useIntl();
  const mechanics = useSelector((state) => state.defectsGtcRedux?.mechanics || []);
  const [repairMode, setRepairMode] = useState(initialMode || REPAIR_MODE.REQUIRED);
  const [isCertified, setIsCertified] = useState(false);
  const [formData, setFormData] = useState({
    mechanicId: '',
    soNumber: '',
    comment: '',
    resolution: '',
  });
  const [loading, setLoading] = useState(false);
  const resolutionOptions = [
    { id: 'ADJUSTED', name: 'Adjusted' },
    { id: 'CLEANED', name: 'Cleaned' },
    { id: 'OTHER', name: 'Other' },
    { id: 'REBUILT', name: 'Rebuilt' },
    { id: 'REPAIRED', name: 'Repaired' },
    { id: 'REPLACED', name: 'Replaced' },
    { id: 'SERVICED', name: 'Serviced' },
  ];
  const defectStatuses = {
    [REPAIR_MODE.PENDING]: 'PENDING',
    [REPAIR_MODE.UNNECESSARY]: 'IGNORED',
    [REPAIR_MODE.REQUIRED]: 'REPAIRED',
  }

  useEffect(() => {
    if (open && mechanics.length === 0) {
      dispatch(fetchMechanics());
    }
  }, [dispatch, open, mechanics.length]);

  // Reset repair mode when modal opens with a new initialMode
  useEffect(() => {
    if (open && initialMode) {
      setRepairMode(initialMode);
    }
  }, [open, initialMode]);

  useEffect(() => {
    setIsCertified(false);
  }, [repairMode]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setRepairMode(newMode);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const mechanicName = repairMode === REPAIR_MODE.REQUIRED
        ? mechanics.find((mechanic) => String(mechanic.id) === String(formData.mechanicId))?.name
        : userInfo.fullName;

      // Submit repair for each selected defect
      for (const d of defects) {
        await onSubmit({
          defectId: d.defectId,
          assetId: d.assetId,
          inspectionId: d.inspId,
          mechanicFirstName: mechanicName,
          ...formData,
          ...(repairMode === REPAIR_MODE.REQUIRED ? {} : { mechanicId: userInfo.subUserId }),
          status: defectStatuses[repairMode],
        });
      }
      onClose();
      resetForm();
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      mechanicId: '',
      soNumber: '',
      comment: '',
      resolution: '',
    });
    setRepairMode(initialMode || REPAIR_MODE.REQUIRED);
    setIsCertified(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getCertifyText = (repairMode, name) => {
    switch (repairMode) {
      case REPAIR_MODE.PENDING:
        return `I, ${name}, certify the above defect update is true.`;
      case REPAIR_MODE.UNNECESSARY:
        return `I, ${name}, certify the indicated defects required no repairs.`;
      case REPAIR_MODE.REQUIRED:
        return `I, ${name}, certify the indicated defects have been corrected.`;
      default:
        return '';
    }
  };

  const isRepairValid = repairMode === REPAIR_MODE.REQUIRED
    ? formData.mechanicId && formData.comment && formData.resolution
    : formData.comment;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{messages.addRepair || 'Add Repair'}</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {defects.length > 0 && (
          <Box mb={3} p={2} bgcolor="#f5f5f5" borderRadius={1} sx={{ maxHeight: 200, overflowY: 'auto' }}>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
              {messages.defectDetails || 'Defect Details'} {defects.length > 1 && `(${defects.length})`}
            </Typography>
            {defects.map((d, index) => (
              <Box key={d.defectId || index} sx={{ mb: defects.length > 1 && index < defects.length - 1 ? 1.5 : 0, pb: defects.length > 1 && index < defects.length - 1 ? 1.5 : 0, borderBottom: defects.length > 1 && index < defects.length - 1 ? '1px solid #ddd' : 'none' }}>
                <Typography variant="body2">
                  <strong>{messages.asset || 'Asset'}:</strong> {d.assetFleet}
                </Typography>
                <Typography variant="body2">
                  <strong>{messages.component || 'Component'}:</strong> {d.zoneName} - {d.componentName}
                </Typography>
                <Typography variant="body2">
                  <strong>{messages.condition || 'Condition'}:</strong> {d.conditionName}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Box mb={3}>
          <ToggleButtonGroup
            value={repairMode}
            exclusive
            onChange={handleModeChange}
            fullWidth
            size="small"
          >
            <ToggleButton value={REPAIR_MODE.PENDING}>
              {messages.pending || 'Pending'}
            </ToggleButton>
            <ToggleButton value={REPAIR_MODE.UNNECESSARY}>
              {messages.repairNotNeeded || 'Repair Not Needed'}
            </ToggleButton>
            <ToggleButton value={REPAIR_MODE.REQUIRED}>
              {messages.repaired || 'Repaired'}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {repairMode === REPAIR_MODE.REQUIRED ? (
          <Box className="repair-form">
            <FormControl fullWidth className="form-field">
              <InputLabel>{messages.mechanicRequired || 'Mechanic *'}</InputLabel>
              <Select
                value={formData.mechanicId}
                label={messages.mechanicRequired || 'Mechanic *'}
                onChange={(e) => handleChange('mechanicId', e.target.value)}
              >
                {mechanics.map((mech) => (
                  <MenuItem key={mech.id} value={mech.id}>
                    {mech.firstName} {mech.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <div style={{ paddingTop: 16 }}>
              <FormControl fullWidth className="form-field">
                <InputLabel>{messages.resolutionRequired || 'Resolution *'}</InputLabel>
                <Select
                  value={formData.resolution}
                  label={messages.mechanicRequired || 'Mechanic *'}
                  onChange={(e) => handleChange('resolution', e.target.value)}
                >
                  {resolutionOptions.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <TextField
              fullWidth
              className="form-field"
              label={messages.serviceOrderNumber || 'Service Order Number'}
              value={formData.soNumber}
              onChange={(e) => handleChange('soNumber', e.target.value)}
              sx={{ mt: 2 }}
            />

            <TextField
              fullWidth
              className="form-field"
              label={messages.repairNotesRequired || 'Repair Notes *'}
              multiline
              rows={4}
              value={formData.comment}
              onChange={(e) => handleChange('comment', e.target.value)}
              placeholder={messages.describeRepairPerformed || 'Describe the repair performed...'}
              inputProps={{ maxLength: MAX_COMMENT_LENGTH }}
              helperText={`${formData.comment.length}/${MAX_COMMENT_LENGTH}`}
              sx={{ mt: 2 }}
            />
          </Box>
        ) : (
          <Box className="no-repair-form">
            <TextField
              fullWidth
              className="form-field"
              label={messages.repairNotesRequired || 'Repair Notes *'}
              multiline
              rows={4}
              value={formData.comment}
              onChange={(e) => handleChange('comment', e.target.value)}
              placeholder={messages.describeRepairPerformed || 'Describe the repair performed...'}
              inputProps={{ maxLength: MAX_COMMENT_LENGTH }}
              helperText={`${formData.comment.length}/${MAX_COMMENT_LENGTH}`}
              sx={{ mt: 2 }}
            />
          </Box>
        )}

        <div style={{ paddingTop: 24 }}>
          <Checkbox
            id={'certify-checkbox'}
            label={getCertifyText(repairMode, userInfo.fullName)}
            checked={isCertified}
            onChange={(checked) => setIsCertified(checked)}
            size='md'
          />
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {messages.cancel || 'Cancel'}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isRepairValid || loading || !isCertified}
        >
          {loading ? (messages.saving || 'Saving...') : (messages.saveRepair || 'Save Repair')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RepairModal;
